import { Link } from "react-router-dom";
import { LOGO, TG_LOGO, CONTACT_EMAIL, VIDEOS, testguardHref, campuspathHref } from "@/lib/constants";
import { TGWordmark } from "@/components/TGWordmark";
import VideoBackground from "@/components/VideoBackground";

export default function SiteFooter({ dark = false }) {
  const bg = dark ? "bg-[#0B1526] text-white" : "bg-[#0a3a66] text-white";
  const video = dark ? VIDEOS.examComputer : VIDEOS.hallway;
  const overlay = dark
    ? "bg-gradient-to-b from-[#0B1526]/75 via-[#0B1526]/55 to-[#0B1526]/92"
    : "bg-gradient-to-b from-[#0a3a66]/72 via-[#0a3a66]/50 to-[#0a3a66]/92";

  return (
    <footer className={`relative overflow-hidden ${bg}`} data-testid="site-footer">
      <VideoBackground
        src={video.src}
        poster={video.poster}
        videoClassName={`${dark ? "object-[center_30%]" : "object-top"} opacity-45 [filter:grayscale(15%)]`}
        overlayClassName={overlay}
      />

      <div className="relative z-10 mx-auto max-w-7xl px-6 pt-20 pb-10 md:px-8">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <span className="relative inline-flex items-center justify-center">
                <span className="absolute -inset-2 rounded-2xl bg-white/70 blur-lg" aria-hidden="true" />
                <img
                  src={dark ? TG_LOGO : LOGO}
                  alt={dark ? "TestGuard" : "CampusPath"}
                  className="relative h-16 w-auto object-contain drop-shadow-[0_0_10px_rgba(255,255,255,0.65)]"
                  data-testid="footer-logo"
                />
              </span>
              {dark ? (
                <TGWordmark className="font-display text-2xl font-800 tracking-tight" />
              ) : (
                <span className="font-display text-2xl font-800 tracking-tight">
                  Campus<span className="text-leaf">Path</span>
                </span>
              )}
            </div>
            <p className="mt-6 max-w-sm text-lg leading-relaxed text-white/70">
              {dark
                ? "Secure exams with full browser lockdown, live device monitoring and a proctor dashboard, built to keep every test session honest."
                : "A school's daily operations, arrival, dismissal, attendance and family communication, in one platform."}
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/trial" className="rounded-full bg-leaf px-6 py-3 text-sm font-700 text-ink transition-colors duration-300 hover:bg-white" data-testid="footer-trial-btn">
                Start Free Trial
              </Link>
              <Link to="/demo" className="rounded-full border border-white/25 px-6 py-3 text-sm font-700 transition-colors duration-300 hover:border-white hover:bg-white hover:text-ink" data-testid="footer-demo-btn">
                Schedule Demo
              </Link>
            </div>
          </div>

          <div className="md:col-span-2 md:col-start-8">
            <div className="text-xs uppercase tracking-widest text-white/50">Platform</div>
            <ul className="mt-5 space-y-3 text-white/70">
              {dark ? (
                <>
                  <li><a href="/#platform" className="transition-colors hover:text-leaf">Browser Lockdown</a></li>
                  <li><a href="/#platform" className="transition-colors hover:text-leaf">Device Monitoring</a></li>
                  <li><a href="/#platform" className="transition-colors hover:text-leaf">Proctor Dashboard</a></li>
                  <li><a href={campuspathHref()} className="transition-colors hover:text-leaf" data-testid="footer-campuspath-link">CampusPath</a></li>
                </>
              ) : (
                <>
                  <li><a href="/#platform" className="transition-colors hover:text-leaf">Dismissal</a></li>
                  <li><a href="/#platform" className="transition-colors hover:text-leaf">Attendance</a></li>
                  <li><a href="/#platform" className="transition-colors hover:text-leaf">Gradebook</a></li>
                  <li><a href={testguardHref()} className="transition-colors hover:text-leaf" data-testid="footer-testguard-link">TestGuard</a></li>
                </>
              )}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-widest text-white/50">Company</div>
            <ul className="mt-5 space-y-3 text-white/70">
              <li><a href="/#security" className="transition-colors hover:text-leaf">Security</a></li>
              <li><a href="/#stories" className="transition-colors hover:text-leaf">Success Stories</a></li>
              <li><a href={`mailto:${CONTACT_EMAIL}`} className="transition-colors hover:text-leaf">Contact Sales</a></li>
              <li>
                <Link
                  to="/admin"
                  className="font-600 text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.5)] transition-colors hover:text-leaf"
                  data-testid="footer-team-login"
                >
                  Team Login
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 text-sm text-white/50 md:flex-row md:items-center">
          <span>© {new Date().getFullYear()} CampusPath. campuspath.org · testguard.org</span>
          <span>{dark ? "Secure Exams. Verified Integrity." : "Smart Dismissal. Safer Schools."}</span>
        </div>
      </div>
    </footer>
  );
}
